import {
  PersonaInquiryLifecycleStatus,
  PersonaInquiryDecisionStatus,
  DocusignEnrollmentAgreement,
} from '@sandtech-hub-monorepo/ehub-client';
import { Application, Enrollment } from '../types';

export enum VerificationStatus {
  NotSubmitted = 'NOT_SUBMITTED',
  Pending = 'PENDING',
  Failed = 'FAILED',
  Completed = 'COMPLETED',
  Resume = 'RESUME',
}

export interface VerificationState {
  identityVerification: VerificationStatus;
  secondaryEducation: VerificationStatus;
  // priorLearning: VerificationStatus;
  enrollmentAgreement: VerificationStatus;
}

export type PersonaInquiry = Application['personaIdentityInquiry'];

/**
 * Maps Persona inquiry lifecycle and decision status to UI verification status
 */
export const getPersonaInquiryStatus = (
  inquiry: PersonaInquiry
): VerificationStatus => {
  if (!inquiry) {
    return VerificationStatus.NotSubmitted;
  }

  if (inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Expired) {
    return VerificationStatus.Resume;
  }

  if (
    inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Failed ||
    (inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Completed &&
      inquiry.decisionStatus === PersonaInquiryDecisionStatus.Declined)
  ) {
    return VerificationStatus.Failed;
  }

  if (
    inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Completed &&
    inquiry.decisionStatus === PersonaInquiryDecisionStatus.Approved
  ) {
    return VerificationStatus.Completed;
  }

  if (inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Pending) {
    return VerificationStatus.Resume;
  }

  if (
    inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Completed &&
    inquiry.decisionStatus === PersonaInquiryDecisionStatus.NeedsReview
  ) {
    return VerificationStatus.Pending;
  }

  if (inquiry.lifecycleStatus === PersonaInquiryLifecycleStatus.Created) {
    return VerificationStatus.Resume;
  }

  return VerificationStatus.Pending;
};

/**
 * Determines if the DocuSign enrollment agreement has been signed by the applicant.
 * Checks if applicantStatus is 'completed' or 'signed'.
 */
export const isDocusignSigned = (
  docusign: DocusignEnrollmentAgreement | undefined | null
): boolean => {
  if (!docusign) {
    return false;
  }
  const status = docusign.applicantStatus?.toLowerCase();
  return status === 'completed' || status === 'signed';
};

export const getDocusignStatus = (
  docusign: DocusignEnrollmentAgreement | undefined | null
): VerificationStatus => {
  if (!docusign) {
    // No envelope created yet - waiting for admission approval
    return VerificationStatus.Pending;
  }

  // Check if the applicant has signed
  if (isDocusignSigned(docusign)) {
    return VerificationStatus.Completed;
  }

  // Envelope exists but not signed yet - can click to sign
  return VerificationStatus.NotSubmitted;
};

type PaymentPlan = NonNullable<Enrollment['paymentPlan']>;

/**
 * Maps payment plan status to UI verification status for the application
 * requirements "first payment" step.
 *
 * This step tracks whether the learner has made their first payment
 * (the starting fee). Once at least one instalment is paid, the step
 * is considered completed — regardless of the overall plan status.
 */
export const getPaymentPlanStatus = (
  paymentPlan: PaymentPlan,
  isAgreementSigned: boolean
): VerificationStatus => {
  if (!isAgreementSigned) {
    return VerificationStatus.NotSubmitted;
  }

  const hasPaid =
    paymentPlan.instalmentsPaid != null && paymentPlan.instalmentsPaid > 0;

  // If any payment has been made, the "first payment" step is done
  if (hasPaid) {
    return VerificationStatus.Completed;
  }

  // Plan completed with 0 instalments shouldn't happen, but handle gracefully
  if (paymentPlan.status === 'COMPLETED') {
    return VerificationStatus.Completed;
  }

  // ACTIVE, INACTIVE, AT_RISK, or CANCELLED with no payments yet
  return VerificationStatus.NotSubmitted;
};
